import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  GetIntegrationEditFormDataInput,
  useGetIntegrationEditFormDataQuery,
} from 'generated/graphql';
import { useMemo } from 'react';
import { mapEditIntegrationFormData } from 'v2/core/repository/edge-server/integrations/editIntegrations/utils/mapEditIntegrationFormData';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { QueryOptions } from 'v2/core/repository/edge-server/types';

const useGetIntegrationEditFormData = (
  input: GetIntegrationEditFormDataInput,
  options?: QueryOptions,
) => {
  const { showErrorNotification } = useNotificationContext();

  const {
    data,
    loading,
    error,
    called,
    refetch,
    startPolling,
    stopPolling,
    fetchMore,
  } = useGetIntegrationEditFormDataQuery({
    variables: input,
    skip: !input.integrationID || options?.Skip,
    fetchPolicy: options?.FetchPolicy ?? 'cache-and-network',
    nextFetchPolicy: options?.FetchPolicy ?? 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (options?.OnCompleted) {
        options.OnCompleted(data.getIntegrationEditFormData?.formData);
      }
    },
    onError: (error) => {
      showErrorNotification({
        contentComponent: `Failed to fetch integration form data: ${error.message}`,
        props: {},
      });
    },
  });

  const status = apolloStatusToQueryStatus(loading, called, false, error);

  const editIntegrationsFormData = useMemo(() => {
    if (!data?.getIntegrationEditFormData?.formData) {
      return;
    }
    return mapEditIntegrationFormData(
      data?.getIntegrationEditFormData?.formData,
    );
  }, [data?.getIntegrationEditFormData?.formData]);

  return {
    data: editIntegrationsFormData,
    status,
    error,
    refetch,
    startPolling,
    stopPolling,
    fetchMore,
  } as const;
};

export default useGetIntegrationEditFormData;
